import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { getIsLoggedInSelector } from '@americanexpress/travel-selectors';
import { useSelector } from 'react-redux';
import { OnVisible } from '@americanexpress/data-manager-utils';
import { Experiment, Variant } from '@americanexpress/one-experiments';
import { ProgressCircular } from '@americanexpress/dls-react';
import clsx from 'clsx';
import { getOneCMSDomainSelector } from '../../selectors/getDomainSelectors';
import {
  EARLY_TENURE_CONSUMER_ONECMS_IMAGE_PATH,
  EARLY_TENURE_BUSINESS_ONECMS_IMAGE_PATH,
  EARLY_TENURE_CARD_GROUPS,
  EARLY_TENURE_SEGMENT_GROUPS,
} from '../../constants/pathnameConstants';
import { useSegmentationData, useSegmentationApi } from '../../hooks/segmentationApiHooks';
import { useFirstName, useMembershipApi } from '../../hooks/membershipDomainDataHooks';
import { MODULE_NAME } from '../../constants/modules';
import { getIntLinkUrlUtil } from '../../utils/getIntLinkUrlUtil';
import { useFeatureFlagWithProperties } from '../../thunks/useFeatureFlagWithProperties';
import { EARLY_TENURE_FF } from '../../constants/oneXpConstants';
import styles from './styles.css';

const RenderEarlyTenureOrHomeTopColumnTile = ({
  cardType,
  className,
  component: Component,
  travelHomeTopColumnTileContent,
}) => {
  const { formatMessage } = useIntl();
  const isLoggedIn = useSelector(getIsLoggedInSelector);
  const isEarlyTenureTileMarket = useFeatureFlagWithProperties(EARLY_TENURE_FF);
  const oneCmsDomain = useSelector(getOneCMSDomainSelector);
  const { isLoading: membershipApiLoading } = useMembershipApi();
  const { isLoading: segmentationLoading } = useSegmentationApi();
  const {
    cardProductGroup,
    earlyTenureGroup,
    localeId: cardProductGroupLocaleId,
    redirectUrl,
  } = useSegmentationData();
  const firstName = useFirstName();

  if (!Component || typeof Component !== 'function') {
    return null;
  }

  if (membershipApiLoading || segmentationLoading) {
    return (
      <div className={clsx(styles.loader, 'flex flex-align-center pad')}>
        <ProgressCircular size="lg" className="margin-center" />
      </div>
    );
  }

  const businessImageUrl = `${oneCmsDomain}/${EARLY_TENURE_BUSINESS_ONECMS_IMAGE_PATH}`;
  const consumerImageUrl = `${oneCmsDomain}/${EARLY_TENURE_CONSUMER_ONECMS_IMAGE_PATH}`;
  const imageUrl = cardProductGroup?.value?.includes('Business') ? businessImageUrl : consumerImageUrl;

  const hasDisplayEarlyTenure = !!isLoggedIn
    && isEarlyTenureTileMarket
    && EARLY_TENURE_SEGMENT_GROUPS.includes(earlyTenureGroup?.key)
    && EARLY_TENURE_CARD_GROUPS.includes(cardProductGroup?.key);

  const heading = formatMessage({ id: 'earlyTenure.heading' }, { firstName });
  const subHeading = formatMessage({ id: cardProductGroupLocaleId });
  const ctaLabel = formatMessage({ id: 'earlyTenure.ctaLabel' });

  const elementId = `us-travel-early-tenure-tile-${cardProductGroup?.key}`;
  const impressionElement = `hp-Guest-${cardType}card`;

  if (hasDisplayEarlyTenure) {
    return (
      <div className={className} id={elementId}>
        <Experiment
          experimentName="Early Tenure Tile Badge"
          defaultVariant="Without Badge"
          dimensionName="Display Badge"
        >
          <Variant variant="Without Badge">
            <OnVisible
              analyticsInfo={{
                detail: 'L1-no-badge',
                element: impressionElement,
                identifier: MODULE_NAME,
                timestamp: new Date().toISOString(),
                type: 'impression',
              }}
            >
              <Component
                content={{
                  heading,
                  subHeading,
                  ctaLabel,
                  redirectUrl: getIntLinkUrlUtil(redirectUrl, cardType, false),
                  imageUrl,
                  imageLabel: '',
                  imageLocationLabel: '',
                  analyticElement: 'hp-early-tenure-tile-cta',
                  hasDisplayEarlyTenureBadge: false,
                }}
                index={1}
              />
            </OnVisible>
          </Variant>
          <Variant variant="With Badge">
            <OnVisible
              analyticsInfo={{
                detail: 'L1-badge',
                element: impressionElement,
                identifier: MODULE_NAME,
                timestamp: new Date().toISOString(),
                type: 'impression',
              }}
            >
              <Component
                content={{
                  heading,
                  subHeading,
                  ctaLabel,
                  redirectUrl: getIntLinkUrlUtil(redirectUrl, cardType, true),
                  imageUrl,
                  imageLabel: '',
                  imageLocationLabel: '',
                  analyticElement: 'hp-early-tenure-tile-cta',
                  hasDisplayEarlyTenureBadge: true,
                }}
                index={1}
              />
            </OnVisible>
          </Variant>
        </Experiment>
      </div>
    );
  }

  return (
    <Component
      className={className}
      content={travelHomeTopColumnTileContent}
      index={1}
    />
  );
};

RenderEarlyTenureOrHomeTopColumnTile.propTypes = {
  cardType: PropTypes.string.isRequired,
  className: PropTypes.string,
  component: PropTypes.func,
  travelHomeTopColumnTileContent: PropTypes.shape({}).isRequired,
};
export default RenderEarlyTenureOrHomeTopColumnTile;
